/**
 * SurebetViewFilterBar - Client-side filter bar for Arbitrage analysis.
 *
 * Always visible: shown count, search, min-margin chips, active chips.
 * Collapsible panel: time window, championships, markets.
 * Instant client-side apply — no Apply button (unlike operator API panel).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSurebet } from '../context/SurebetContext';
import { useTranslation } from '../../i18n/hooks';
import { MARGIN_CHIP_VALUES } from '../viewFilters/surebetViewFilters';
import SurebetViewTimeWindowFilter from './SurebetViewTimeWindowFilter';
import SurebetViewChampionshipFilter from './SurebetViewChampionshipFilter';
import SurebetViewMarketFilter from './SurebetViewMarketFilter';
import SurebetViewActiveFilterChips from './SurebetViewActiveFilterChips';
import '../../bestOdds/components/BestOddsFilterBar.css';

const SEARCH_DEBOUNCE_MS = 250;
const FILTERS_PANEL_ID = 'surebet-view-filters-panel';

const SurebetViewFilterBar = ({ className = '', children = null }) => {
  const { t } = useTranslation();
  const {
    viewFilters,
    filteredCount,
    totalEvents,
    hasActiveViewFilters,
    setMinMargin,
    setSearchQuery
  } = useSurebet();

  const [searchInput, setSearchInput] = useState(() => viewFilters?.searchQuery || '');
  const [panelOpen, setPanelOpen] = useState(false);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    const nextQuery = viewFilters?.searchQuery || '';
    setSearchInput(nextQuery);

    if (!nextQuery && searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  }, [viewFilters?.searchQuery]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const activeMinMargin = Number(viewFilters?.minMargin) > 0
    ? Number(viewFilters.minMargin)
    : 0;

  const handleMarginChipClick = useCallback((value) => {
    setMinMargin(value);
  }, [setMinMargin]);

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setSearchInput(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, SEARCH_DEBOUNCE_MS);
  }, [setSearchQuery]);

  const handleClearSearch = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setSearchInput('');
    setSearchQuery('');
  }, [setSearchQuery]);

  const handlePanelToggle = useCallback(() => {
    setPanelOpen((prev) => !prev);
  }, []);

  const hasSearchText = searchInput.trim().length > 0;
  const panelHasRestrictedFilters = Array.isArray(viewFilters?.selectedChampionships)
    || Array.isArray(viewFilters?.selectedMarketKeys)
    || (viewFilters?.timeWindow && viewFilters.timeWindow !== 'all');

  const totalCount = typeof totalEvents === 'number' ? totalEvents : 0;

  return (
    <div
      className={`best-odds-filter-bar ${hasActiveViewFilters ? 'best-odds-filter-bar--active' : ''} ${className}`.trim()}
      role="region"
      aria-label={t('surebets.viewFilters.activeFilters')}
    >
      <div className="best-odds-filter-bar__meta">
        <span className="best-odds-filter-bar__count" aria-live="polite">
          {t('surebets.viewFilters.shownCount', {
            filtered: filteredCount,
            total: totalCount
          })}
        </span>
      </div>

      <div className="best-odds-filter-bar__controls">
        <div className="best-odds-filter-bar__search">
          <label className="best-odds-filter-bar__search-label visually-hidden" htmlFor="surebet-view-search">
            {t('surebets.viewFilters.searchAriaLabel')}
          </label>
          <input
            id="surebet-view-search"
            type="text"
            className="best-odds-filter-bar__search-input"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={t('surebets.viewFilters.searchPlaceholder')}
            aria-label={t('surebets.viewFilters.searchAriaLabel')}
            autoComplete="off"
          />
          {hasSearchText && (
            <button
              type="button"
              className="best-odds-filter-bar__search-clear"
              onClick={handleClearSearch}
              aria-label={t('surebets.viewFilters.searchClearAriaLabel')}
              title={t('surebets.viewFilters.searchClear')}
            >
              ×
            </button>
          )}
        </div>

        <div className="best-odds-filter-bar__chip-row">
          <span className="best-odds-filter-bar__chip-row-label" id="surebet-margin-label">
            {t('surebets.viewFilters.marginLabel')}
          </span>
          <div
            className="best-odds-filter-bar__chip-row-chips"
            role="group"
            aria-labelledby="surebet-margin-label"
          >
            {MARGIN_CHIP_VALUES.map((value) => {
              const isAll = value === 0;
              const isActive = activeMinMargin === value;
              const label = isAll
                ? t('surebets.viewFilters.marginAll')
                : t('surebets.viewFilters.marginChip', { value });
              const ariaLabel = isAll
                ? t('surebets.viewFilters.marginAllAriaLabel')
                : t('surebets.viewFilters.marginChipAriaLabel', { value });

              return (
                <button
                  key={value}
                  type="button"
                  className={`best-odds-filter-bar__chip${isActive ? ' is-active' : ''}`}
                  aria-pressed={isActive}
                  aria-label={ariaLabel}
                  onClick={() => handleMarginChipClick(value)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <button
        type="button"
        className={`best-odds-filter-bar__panel-toggle${panelOpen ? ' is-open' : ''}${panelHasRestrictedFilters ? ' has-panel-filters' : ''}`}
        onClick={handlePanelToggle}
        aria-expanded={panelOpen}
        aria-controls={FILTERS_PANEL_ID}
      >
        <span className="best-odds-filter-bar__panel-toggle-text">
          {panelOpen
            ? t('surebets.viewFilters.panelToggleHide')
            : t('surebets.viewFilters.panelToggleShow')}
          {panelHasRestrictedFilters && (
            <span className="best-odds-filter-bar__panel-toggle-dot" aria-hidden="true" />
          )}
        </span>
        <span className="best-odds-filter-bar__panel-toggle-chevron" aria-hidden="true">
          ▾
        </span>
      </button>

      {panelOpen && (
        <div
          id={FILTERS_PANEL_ID}
          className="best-odds-filter-bar__panel"
          role="region"
          aria-label={t('surebets.viewFilters.panelAriaLabel')}
        >
          <SurebetViewTimeWindowFilter />
          <SurebetViewChampionshipFilter />
          <SurebetViewMarketFilter />
          {children}
        </div>
      )}

      <SurebetViewActiveFilterChips />
    </div>
  );
};

SurebetViewFilterBar.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

export default SurebetViewFilterBar;
